import {
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Command as CommandPrimitive } from "cmdk";
import {
  BookOpen,
  Building2,
  Car,
  FileText,
  FolderKanban,
  Layers,
  type LucideIcon,
  Package,
  Radio,
  Search,
  SlidersHorizontal,
  User,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SearchSnippet } from "@/components/meeting-notes/SearchSnippet";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { useProjects } from "@/hooks/useProjects";
import { useProjectGroups } from "@/hooks/useProjectGroups";
import { useCompanies } from "@/hooks/useCompanies";
import { usePeople } from "@/hooks/usePeople";
import { useAllEquipment } from "@/hooks/useEquipment";
import { useVehicles } from "@/hooks/useVehicles";
import { useStudios } from "@/hooks/useStudios";
import { useStudioConfigs } from "@/hooks/useStudioConfigs";
import { useAllMeetingNotes } from "@/hooks/useAllMeetingNotes";
import { useMeetingNoteSearch } from "@/hooks/useMeetingNoteSearch";
import { useGlobalDocuments } from "@/hooks/useGlobalDocuments";
import { useLastOpenedProjects } from "@/hooks/useLastOpenedProjects";
import { formatProjectName } from "@/lib/format-project-name";
import { buildSearchIndex, searchIndex } from "@/lib/search-utils";
import { AppLink, isPlainLeftClick } from "@/navigation/AppLink";
import {
  companyHref,
  docHref,
  equipmentHref,
  meetingNoteHref,
  personHref,
  projectGroupHref,
  projectHref,
  studioConfigHref,
  studioHref,
  vehicleHref,
} from "@/navigation/hrefs";
import { KEEP_ORIGIN_STATE } from "@/navigation/origin";
import { usePanel } from "@/navigation/panel";

export const PROJECT_SWITCHER_OPEN_EVENT = "project-switcher:open";
/** Opens global search (⌘K). */
export const GLOBAL_SEARCH_OPEN_EVENT = PROJECT_SWITCHER_OPEN_EVENT;

/** Results shown per entity type (projects get a few more). */
const GROUP_LIMIT = 6;
const PROJECT_LIMIT = 10;
const CONFIGS_PER_STUDIO = 4;

const UUID_LIKE = /^[0-9a-f]{8}-|^[0-9a-f-]{20,}$/i;

/**
 * Section path of the current project page that also exists in another
 * project, e.g. "people/functions" (child pages such as a quote are dropped).
 */
function projectSectionPath(pathname: string) {
  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] !== "projects" || parts.length < 3) return "";
  const section = parts.slice(2, 4);
  if (section[1] && UUID_LIKE.test(section[1])) section.pop();
  return section.join("/");
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    target.tagName === "INPUT" ||
    target.tagName === "TEXTAREA" ||
    target.tagName === "SELECT"
  );
}

type OpenMode = "main" | "tab" | "panel";

interface SearchResult {
  /** Unique across all groups, also the cmdk value. */
  key: string;
  href: string;
  title: string;
  subtitle?: ReactNode;
  icon: LucideIcon;
  /** Shown on the right, e.g. "Recent". */
  hint?: string;
  /** Navigation state for the main view (project switcher). */
  state?: unknown;
}

interface GroupProps {
  query: string;
  onOpen: (result: SearchResult, mode: OpenMode) => void;
}

function ResultItem({
  result,
  onOpen,
}: {
  result: SearchResult;
  onOpen: GroupProps["onOpen"];
}) {
  const Icon = result.icon;
  const handleClick = (event: ReactMouseEvent<HTMLAnchorElement>) => {
    if (isPlainLeftClick(event)) {
      // cmdk's onSelect opens it (and closes the dialog).
      event.preventDefault();
      return;
    }
    // K2: modifier clicks open a new tab natively; don't select the item.
    event.stopPropagation();
  };

  return (
    <CommandPrimitive.Item
      value={result.key}
      onSelect={() => onOpen(result, "main")}
      data-href={result.href}
      className="relative flex cursor-default select-none items-center rounded-sm px-2 py-2 text-sm outline-none data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
    >
      <AppLink
        to={result.href}
        openIn="main"
        tabIndex={-1}
        onClick={handleClick}
        className="flex min-w-0 flex-1 items-center gap-2"
        data-testid="global-search-result"
      >
        <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate">{result.title}</span>
          {result.subtitle ? (
            <span className="truncate text-xs text-muted-foreground">
              {result.subtitle}
            </span>
          ) : null}
        </span>
        {result.hint ? (
          <span className="ml-auto shrink-0 text-xs tracking-widest text-muted-foreground">
            {result.hint}
          </span>
        ) : null}
      </AppLink>
    </CommandPrimitive.Item>
  );
}

function ResultGroup({
  heading,
  results,
  total,
  onOpen,
}: {
  heading: string;
  results: SearchResult[];
  total?: number;
  onOpen: GroupProps["onOpen"];
}) {
  if (results.length === 0) return null;
  const more = total !== undefined && total > results.length;
  return (
    <CommandPrimitive.Group
      heading={more ? `${heading} (${results.length} of ${total})` : heading}
      className="overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
    >
      {results.map((result) => (
        <ResultItem key={result.key} result={result} onOpen={onOpen} />
      ))}
    </CommandPrimitive.Group>
  );
}

// ── Projects (with recent projects and the project switcher mode) ──

function ProjectResults({
  query,
  onOpen,
  keepSection,
  showAllWhenEmpty,
}: GroupProps & { keepSection: boolean; showAllWhenEmpty: boolean }) {
  const { data: projects = [] } = useProjects();
  const { lastOpenedIds } = useLastOpenedProjects();
  const location = useLocation();
  const section = keepSection ? projectSectionPath(location.pathname) : "";

  const recentIds = useMemo(() => new Set(lastOpenedIds), [lastOpenedIds]);
  // Recent projects first, in last-opened order.
  const ordered = useMemo(() => {
    const byId = new Map(projects.map((project) => [project.id, project]));
    const recent = lastOpenedIds
      .map((id) => byId.get(id))
      .filter((project) => project !== undefined);
    return [
      ...recent,
      ...projects.filter((project) => !recentIds.has(project.id)),
    ];
  }, [lastOpenedIds, projects, recentIds]);

  const index = useMemo(
    () =>
      buildSearchIndex(
        ordered,
        (project) => formatProjectName(project.name, project.event_start_date),
        (project) => [project.name, project.number, project.companies?.name],
      ),
    [ordered],
  );

  const toResult = (
    project: (typeof projects)[number],
    hint?: string,
  ): SearchResult => {
    const href = section
      ? `${projectHref(project.id)}/${section}`
      : projectHref(project.id);
    return {
      key: `project:${project.id}`,
      href,
      title: formatProjectName(project.name, project.event_start_date),
      subtitle:
        [project.number, project.companies?.name].filter(Boolean).join(" · ") ||
        null,
      icon: FolderKanban,
      hint,
      state: keepSection ? KEEP_ORIGIN_STATE : undefined,
    };
  };

  if (query) {
    const { items, total } = searchIndex(
      index,
      query,
      keepSection ? 50 : PROJECT_LIMIT,
    );
    return (
      <ResultGroup
        heading="Projects"
        total={total}
        results={items.map((project) =>
          toResult(project, recentIds.has(project.id) ? "Recent" : undefined),
        )}
        onOpen={onOpen}
      />
    );
  }

  const recent = ordered.filter((project) => recentIds.has(project.id));
  const rest = ordered.filter((project) => !recentIds.has(project.id));
  return (
    <>
      <ResultGroup
        heading="Recent Projects"
        results={recent.map((project, i) =>
          toResult(project, i === 0 ? "Last opened" : undefined),
        )}
        onOpen={onOpen}
      />
      {showAllWhenEmpty && (
        <ResultGroup
          heading="All Projects"
          results={rest.map((project) => toResult(project))}
          onOpen={onOpen}
        />
      )}
    </>
  );
}

// ── Other entity types (employees only) ──

function useGroupResults<T>(
  items: T[],
  query: string,
  getTitle: (item: T) => string,
  getText: (item: T) => (string | null | undefined)[],
  toResult: (item: T) => SearchResult,
  limit = GROUP_LIMIT,
) {
  // The getters are inline lambdas: index only when the data changes.
  const getters = useRef({ getTitle, getText });
  getters.current = { getTitle, getText };
  const index = useMemo(
    () =>
      buildSearchIndex(
        items,
        (item) => getters.current.getTitle(item),
        (item) => getters.current.getText(item),
      ),
    [items],
  );
  const { items: found, total } = useMemo(
    () => searchIndex(index, query, limit),
    [index, query, limit],
  );
  return { results: found.map(toResult), total };
}

function ProjectGroupResults({ query, onOpen }: GroupProps) {
  const { data = [] } = useProjectGroups();
  const { results, total } = useGroupResults(
    data,
    query,
    (group) => group.name,
    (group) => [group.number, group.companies?.name],
    (group) => ({
      key: `project-group:${group.id}`,
      href: projectGroupHref(group.id),
      title: group.name,
      subtitle:
        [group.number, group.companies?.name].filter(Boolean).join(" · ") ||
        null,
      icon: Layers,
    }),
  );
  return (
    <ResultGroup
      heading="Project groups"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function CompanyResults({ query, onOpen }: GroupProps) {
  const { data = [] } = useCompanies();
  const { results, total } = useGroupResults(
    data,
    query,
    (company) => company.name,
    (company) => [company.number],
    (company) => ({
      key: `company:${company.id}`,
      href: companyHref(company.id),
      title: company.name,
      subtitle: company.number,
      icon: Building2,
    }),
  );
  return (
    <ResultGroup
      heading="Companies"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

const PERSON_TYPE_LABELS: Record<string, string> = {
  employee: "Employee",
  freelancer: "Freelancer",
  contact: "Contact",
};

function PeopleResults({ query, onOpen }: GroupProps) {
  const { data = [] } = usePeople();
  const { results, total } = useGroupResults(
    data,
    query,
    (person) => `${person.first_name} ${person.last_name}`.trim(),
    (person) => [person.number, person.email, person.companies?.name],
    (person) => ({
      key: `person:${person.id}`,
      href: personHref(person.id),
      title: `${person.first_name} ${person.last_name}`.trim(),
      subtitle:
        [
          PERSON_TYPE_LABELS[person.person_type] ?? person.person_type,
          person.companies?.name,
        ]
          .filter(Boolean)
          .join(" · ") || null,
      icon: User,
    }),
  );
  return (
    <ResultGroup
      heading="People"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function EquipmentResults({ query, onOpen }: GroupProps) {
  const { data } = useAllEquipment();
  const items = useMemo(
    () => (data ?? []).filter((item) => !item.deleted_at),
    [data],
  );
  const { results, total } = useGroupResults(
    items,
    query,
    (item) => item.name,
    (item) => [item.number, item.manufacturer],
    (item) => ({
      key: `equipment:${item.id}`,
      href: equipmentHref(item.id),
      title: item.name,
      subtitle:
        [item.number, item.manufacturer].filter(Boolean).join(" · ") || null,
      icon: Package,
    }),
  );
  return (
    <ResultGroup
      heading="Equipment"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function VehicleResults({ query, onOpen }: GroupProps) {
  const { data = [] } = useVehicles();
  const { results, total } = useGroupResults(
    data,
    query,
    (vehicle) => vehicle.name,
    (vehicle) => [vehicle.licensePlate, vehicle.brand, vehicle.model],
    (vehicle) => ({
      key: `vehicle:${vehicle.id}`,
      href: vehicleHref(vehicle.id),
      title: vehicle.name,
      subtitle:
        [vehicle.licensePlate, [vehicle.brand, vehicle.model].join(" ").trim()]
          .filter(Boolean)
          .join(" · ") || null,
      icon: Car,
    }),
  );
  return (
    <ResultGroup
      heading="Vehicles"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function StudioResults({ query, onOpen }: GroupProps) {
  const { data = [] } = useStudios();
  const { results, total } = useGroupResults(
    data,
    query,
    (studio) => studio.name,
    (studio) => [studio.number, studio.location],
    (studio) => ({
      key: `studio:${studio.id}`,
      href: studioHref(studio.id),
      title: studio.name,
      subtitle:
        [studio.number, studio.location].filter(Boolean).join(" · ") || null,
      icon: Radio,
    }),
  );
  return (
    <ResultGroup
      heading="Studios"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

/** Configs are loaded per studio; there are only a few studios. */
function StudioConfigsOfStudio({
  studioId,
  studioName,
  query,
  onOpen,
}: GroupProps & { studioId: string; studioName: string }) {
  const { data = [] } = useStudioConfigs(studioId);
  const { results } = useGroupResults(
    data,
    query,
    (config) => config.name,
    (config) => [studioName, config.project_name, config.kind],
    (config) => ({
      key: `studio-config:${config.id}`,
      href: studioConfigHref(studioId, config.id),
      title: config.name,
      subtitle:
        [studioName, config.project_name, config.kind]
          .filter(Boolean)
          .join(" · ") || null,
      icon: SlidersHorizontal,
    }),
    CONFIGS_PER_STUDIO,
  );
  return results.map((result) => (
    <ResultItem key={result.key} result={result} onOpen={onOpen} />
  ));
}

function StudioConfigResults({ query, onOpen }: GroupProps) {
  const { data: studios = [] } = useStudios();
  if (studios.length === 0) return null;
  return (
    <CommandPrimitive.Group
      heading="Studio configs"
      className="[&:not(:has([cmdk-item]))]:hidden overflow-hidden p-1 text-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-muted-foreground"
    >
      {studios.map((studio) => (
        <StudioConfigsOfStudio
          key={studio.id}
          studioId={studio.id}
          studioName={studio.name}
          query={query}
          onOpen={onOpen}
        />
      ))}
    </CommandPrimitive.Group>
  );
}

function MeetingNoteResults({ query, onOpen }: GroupProps) {
  const { data = [] } = useAllMeetingNotes();
  const { snippets } = useMeetingNoteSearch(query);
  const { results: localResults, total: localTotal } = useGroupResults(
    data,
    query,
    (note) => note.title || "Untitled",
    (note) => [note.project?.name, note.project_group?.name, note.meeting_date],
    (note) => ({
      key: `meeting-note:${note.id}`,
      href: meetingNoteHref(note),
      title: note.title || "Untitled",
      subtitle:
        [
          note.meeting_date,
          note.project?.name ?? note.project_group?.name ?? null,
        ]
          .filter(Boolean)
          .join(" · ") || null,
      icon: FileText,
    }),
  );
  // Title matches first, then notes the server found in their full text.
  const { results, total } = useMemo(() => {
    const shown = new Set(localResults.map((result) => result.key));
    const textResults = data
      .filter(
        (note) =>
          snippets.has(note.id) && !shown.has(`meeting-note:${note.id}`),
      )
      .map((note): SearchResult => {
        const snippet = snippets.get(note.id);
        return {
          key: `meeting-note:${note.id}`,
          href: meetingNoteHref(note),
          title: note.title || "Untitled",
          subtitle: snippet ? (
            <SearchSnippet snippet={snippet} />
          ) : (
            note.meeting_date
          ),
          icon: FileText,
        };
      });
    const visibleMatches = data.filter((note) => snippets.has(note.id)).length;
    return {
      results: [...localResults, ...textResults].slice(0, GROUP_LIMIT),
      total: Math.max(localTotal, visibleMatches),
    };
  }, [data, localResults, localTotal, snippets]);
  return (
    <ResultGroup
      heading="Meeting notes"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function DocResults({ query, onOpen }: GroupProps) {
  const { data } = useGlobalDocuments();
  const { docs, folderTitles } = useMemo(
    () => ({
      docs: data.filter((doc) => !doc.is_folder),
      folderTitles: new Map(
        data.filter((doc) => doc.is_folder).map((doc) => [doc.id, doc.title]),
      ),
    }),
    [data],
  );
  const folderOf = (doc: (typeof docs)[number]) =>
    (doc.parent_id && folderTitles.get(doc.parent_id)) ||
    (doc.bucket === "managers" ? "Management" : null);
  const { results, total } = useGroupResults(
    docs,
    query,
    (doc) => doc.title || "Untitled",
    (doc) => [folderOf(doc)],
    (doc) => ({
      key: `doc:${doc.id}`,
      href: docHref(doc.id),
      title: doc.title || "Untitled",
      subtitle: folderOf(doc),
      icon: BookOpen,
    }),
  );
  return (
    <ResultGroup
      heading="Docs"
      results={results}
      total={total}
      onOpen={onOpen}
    />
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1 font-mono text-[10px] text-muted-foreground">
      {children}
    </kbd>
  );
}

/**
 * Global search (⌘K, S1/S2): projects, project groups, companies, people,
 * equipment, vehicles, studios, studio configs, meeting notes and docs.
 * Results are links: Enter/click opens the main view, ⌘/Ctrl+Enter (or a
 * modifier click) a new tab, Shift+Enter the side panel.
 *
 * Opened with `{ keepSection: true }` (the project context's switcher) it
 * only lists projects and switches to the same section of the chosen one.
 */
export function GlobalSearch() {
  const navigate = useNavigate();
  const panel = usePanel();
  const { isEmployee } = useAuth();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim();
  // Opened from a project's context switcher: stay in the same section and
  // keep the visit's origin.
  const [keepSection, setKeepSection] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isEditableTarget(event.target)) {
        return;
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setKeepSection(false);
        setOpen((current) => !current);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<{ keepSection?: boolean }>).detail;
      setKeepSection(!!detail?.keepSection);
      setOpen(true);
    };

    window.addEventListener(PROJECT_SWITCHER_OPEN_EVENT, onOpen);
    return () =>
      window.removeEventListener(PROJECT_SWITCHER_OPEN_EVENT, onOpen);
  }, []);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setQuery("");
    }
  };

  const openResult = useCallback(
    (result: Pick<SearchResult, "href" | "state">, mode: OpenMode) => {
      if (mode === "tab") {
        window.open(result.href, "_blank", "noopener");
        return;
      }
      setOpen(false);
      setQuery("");
      if (mode === "panel") {
        panel.open(result.href);
        return;
      }
      navigate(result.href, result.state ? { state: result.state } : {});
    },
    [navigate, panel],
  );

  // Cmd/Ctrl+Enter and Shift+Enter act on the highlighted result.
  const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter") return;
    const mode: OpenMode | null =
      event.metaKey || event.ctrlKey ? "tab" : event.shiftKey ? "panel" : null;
    if (!mode) return;
    const href = listRef.current?.querySelector<HTMLElement>(
      '[cmdk-item][data-selected="true"]',
    )?.dataset.href;
    if (!href) return;
    event.preventDefault();
    openResult({ href }, mode);
  };

  const placeholder = keepSection
    ? "Switch to project…"
    : isEmployee
      ? "Search projects, people, equipment, docs…"
      : "Search projects by name, number, or company...";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "overflow-hidden p-0 shadow-lg gap-0",
          // Near the top so the on-screen keyboard leaves room (P6).
          "top-2 translate-y-0 w-[calc(100%-1rem)] rounded-lg sm:top-[12vh] sm:max-w-xl",
          "data-[state=open]:slide-in-from-top-2 data-[state=closed]:slide-out-to-top-2",
        )}
        data-testid="global-search"
      >
        <DialogTitle className="sr-only">Search</DialogTitle>
        <CommandPrimitive
          shouldFilter={false}
          loop
          onKeyDown={handleKeyDown}
          className="flex h-full w-full flex-col overflow-hidden rounded-md bg-popover text-popover-foreground"
        >
          <div
            className="flex items-center border-b px-3 pr-10"
            cmdk-input-wrapper=""
          >
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <CommandPrimitive.Input
              value={query}
              onValueChange={setQuery}
              placeholder={placeholder}
              className="flex h-12 w-full rounded-md bg-transparent py-3 text-base outline-none placeholder:text-muted-foreground sm:text-sm"
              data-testid="global-search-input"
            />
          </div>
          <CommandPrimitive.List
            ref={listRef}
            className="max-h-[calc(100dvh-8rem)] overflow-y-auto overflow-x-hidden sm:max-h-[min(60vh,480px)]"
          >
            <CommandPrimitive.Empty className="py-6 text-center text-sm">
              {trimmedQuery ? "No results found." : "Type to search."}
            </CommandPrimitive.Empty>
            {open && (
              <>
                <ProjectResults
                  query={trimmedQuery}
                  onOpen={openResult}
                  keepSection={keepSection}
                  showAllWhenEmpty={keepSection || !isEmployee}
                />
                {isEmployee && !keepSection && trimmedQuery && (
                  <>
                    <ProjectGroupResults
                      query={trimmedQuery}
                      onOpen={openResult}
                    />
                    <CompanyResults query={trimmedQuery} onOpen={openResult} />
                    <PeopleResults query={trimmedQuery} onOpen={openResult} />
                    <EquipmentResults
                      query={trimmedQuery}
                      onOpen={openResult}
                    />
                    <VehicleResults query={trimmedQuery} onOpen={openResult} />
                    <StudioResults query={trimmedQuery} onOpen={openResult} />
                    <StudioConfigResults
                      query={trimmedQuery}
                      onOpen={openResult}
                    />
                    <MeetingNoteResults
                      query={trimmedQuery}
                      onOpen={openResult}
                    />
                    <DocResults query={trimmedQuery} onOpen={openResult} />
                  </>
                )}
              </>
            )}
          </CommandPrimitive.List>
          <div className="hidden items-center gap-3 border-t px-3 py-2 text-xs text-muted-foreground sm:flex">
            <span className="flex items-center gap-1">
              <Kbd>↵</Kbd> open
            </span>
            <span className="flex items-center gap-1">
              <Kbd>⌘/Ctrl ↵</Kbd> new tab
            </span>
            <span className="flex items-center gap-1">
              <Kbd>⇧ ↵</Kbd> side panel
            </span>
          </div>
        </CommandPrimitive>
      </DialogContent>
    </Dialog>
  );
}
